/**
 * Admin hooks for user captures management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export interface AdminCapture {
  id: string;
  species: string | null;
  species_common_name: string | null;
  description: string | null;
  is_public: boolean;
  capture_date: string | null;
  created_at: string;
  promoted_at: string | null;
  image_url: string | null;
  likes_count: number;
  dislikes_count: number;
  comments_count: number;
}

export function useAdminUserCaptures(userId: string | null, options?: { limit?: number; offset?: number }) {
  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;

  return useQuery<AdminCapture[]>({
    queryKey: ['admin', 'user-captures', userId, limit, offset],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await dbClient.rpc('admin_get_user_captures', {
        p_user_id: userId,
        p_limit: limit,
        p_offset: offset,
      });
      if (error) throw error;
      const normalized = normalizeRpcResponse(data);
      return normalized as AdminCapture[];
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useAdminDeleteCapture(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (captureId: string) => {
      const { error } = await dbClient.rpc('admin_delete_capture', { p_capture_id: captureId });
      if (error) throw error;
    },
    onSuccess: (_, __) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-captures', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
    },
  });
}

export function useAdminUpdateCapture(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { captureId: string; description?: string; is_public?: boolean; species?: string }) => {
      const payload: Record<string, unknown> = { p_capture_id: params.captureId };
      if (params.description !== undefined) payload.p_description = params.description;
      if (params.is_public !== undefined) payload.p_is_public = params.is_public;
      if (params.species !== undefined) payload.p_species = params.species;
      const { error } = await dbClient.rpc('admin_update_capture', payload);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-captures', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
    },
  });
}

export function useAdminPromoteCapture(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (captureId: string) => {
      const { error } = await dbClient.rpc('admin_promote_capture_to_feed', { p_capture_id: captureId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-captures', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
    },
  });
}

export function useAdminUpdateCaptureComment(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { commentId: string; text: string }) => {
      const { error } = await dbClient.rpc('admin_update_capture_comment', {
        p_comment_id: params.commentId,
        p_text: params.text,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-captures', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
    },
  });
}

export function useAdminBanUser(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await dbClient.rpc('admin_ban_user', { p_user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'list-users'] });
    },
  });
}

export function useAdminUnbanUser(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await dbClient.rpc('admin_unban_user', { p_user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'list-users'] });
    },
  });
}

export function useAdminPromoteToAdmin(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await dbClient.rpc('admin_promote_to_admin', { p_user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'list-users'] });
    },
  });
}

export function useAdminDemoteFromAdmin(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await dbClient.rpc('admin_demote_from_admin', { p_user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-profile-details', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'list-users'] });
    },
  });
}

export interface AdminCaptureComment {
  id: string;
  text: string;
  user_id: string;
  author_name: string;
  created_at: string;
}

export function useAdminCaptureComments(captureId: string | null) {
  return useQuery<AdminCaptureComment[]>({
    queryKey: ['admin', 'capture-comments', captureId],
    queryFn: async () => {
      if (!captureId) return [];
      const { data, error } = await dbClient.rpc('admin_get_capture_comments', { p_capture_id: captureId });
      if (error) throw error;
      const normalized = normalizeRpcResponse(data);
      return normalized as AdminCaptureComment[];
    },
    enabled: !!captureId,
    staleTime: 30_000,
  });
}
